import { isCollage } from '@mixtiles/web-backend-shared'
import uniq from 'lodash/uniq'
import {
  MIXED_MATERIALS,
  MIXED_SIZES,
  OUT_OF_STOCK,
} from '../../pages/PhotoStyler/TileDesignerConsts'
import { useTiles } from '../useTiles'
import { useBulkCustomization } from '../../components/BulkCustomizationProvider/BulkCustomizationProvider'
import useDialog from '../../elements/Dialog/DialogProvider'
import BulkOverrideWarningDialog from '../../pages/PhotoStyler/BulkOverrideWarningDialog/BulkOverrideWarningDialog'
import { shouldUpdateCollageLayout } from '../../services/Collage/layoutFetcher'
import { usePricing } from '../../components/Pricing/PricingProvider'
import { isMixedSizesDiscount } from '../../components/Pricing/Discounts/Discounts'
import { DISCOUNT_TYPES } from '../../stores/promoCodeStore/promoCode.consts'
import { useCheckout } from '../../components/Checkout/CheckoutContext'
import { analytics, EVENT_NAMES } from '../../services/Analytics/Analytics'
import { getOrderedSizes } from '../../pages/DiscoveryPage/DiscoveryConsts'
import { getTileStyleData } from '../../services/tilesStyling'
import { getSizeName } from '../../services/SizesManager'
import { closeSnackbar } from 'notistack'
import { useAvailableFrames } from '../../components/FrameProvider/AvailableStockProvider'
import scrollIntoView from 'scroll-into-view-if-needed'
import {
  OUT_OF_STOCK_NOTIFICATION_TYPE,
  OUT_OF_STOCK_PROPERTY,
  useOutOfStockNotification,
} from '../../elements/Snackbar/OutOfStockNotification/useOutOfStockNotification'
import { useTilesRefs } from '../../components/TilesRefsProvider/TilesRefsProvider'
import { FRAME_COLORS } from '../../core/Tile/consts'
import { isFramedMaterial } from '../../utils/materialTypeUtils'
import { getDefaultTileSize } from './utils'
import difference from 'lodash/difference'
import {
  getMaterialToName,
  getTilesMaterials,
} from '../../services/tileMaterial'
import { isSingleSizeOrder } from '../../components/PhotoBank/tileUtils'
import { useTranslation } from 'react-i18next'
import { TILE_CUSTOMIZATION } from '../../pages/PhotoStyler/tileCustomization.consts'

export default function useTileSize() {
  const { bulkCustomization, updateBulk } = useBulkCustomization()
  const {
    tiles,
    nonCenterpieceTiles,
    changeTileSize,
    changeTileSizeToAll,
    updateTiles,
  } = useTiles()
  const { saveTilesState, undoTiles, getRef } = useTilesRefs()
  const dialog = useDialog()
  const { fetchDiscount } = usePricing()
  const { discountId } = useCheckout()
  const { getSupportedFramesForSize, getUnsupportedTilesForSize } =
    useAvailableFrames()
  const supportedSizes = getOrderedSizes(bulkCustomization.materialType)
  const { notifyOutOfStock } = useOutOfStockNotification()
  const { isSupportedSizeForMaterial } = useAvailableFrames()
  const { t } = useTranslation()
  const materialToName = getMaterialToName(t)

  const matchTilesSizeWithTiles = () => {
    let actualTileSize
    const { tileSize } = bulkCustomization
    // If no tiles or only centerpieces, return the default size
    if (nonCenterpieceTiles.length === 0) {
      const defaultTileSize = getDefaultTileSize()
      actualTileSize =
        tileSize === MIXED_SIZES || !supportedSizes.includes(tileSize)
          ? defaultTileSize
          : tileSize
    } else if (isSingleSizeOrder(nonCenterpieceTiles)) {
      actualTileSize = nonCenterpieceTiles[0]?.tileSize || tileSize
    } else {
      actualTileSize = MIXED_SIZES
    }
    if (actualTileSize !== tileSize) {
      updateBulk.tileSize(actualTileSize)
    }

    return actualTileSize
  }

  const updateTileSize = ({ tileId, newTileSize }) => {
    const oldTileSize = bulkCustomization.tileSize
    const updateSize = () => {
      verifyStock({
        tile,
        tileSize: newTileSize,
        materialType: tile.materialType,
      })
      changeTileSize(tileId, newTileSize)
    }
    const tile = tiles.find((t) => t.id === tileId)
    if (
      tile &&
      isCollage(tile) &&
      shouldUpdateCollageLayout(tile, newTileSize)
    ) {
      updateTiles([tileId], (t) => ({ ...t, isProcessing: true }))
    }

    // Transition back to specific size if all sizes now are the same
    const restTiles = nonCenterpieceTiles.filter((tile) => tile.id !== tileId)
    const restTilesSizes = uniq(restTiles.map((tile) => tile.tileSize))
    if (
      oldTileSize === MIXED_SIZES &&
      (restTilesSizes.length === 0 ||
        (isSingleSizeOrder(restTiles) && restTilesSizes[0] === newTileSize))
    ) {
      // Changing one tile size caused us to have all tiles with the same size
      updateSize()
      updateBulk.tileSize(newTileSize)
      return
    }

    // Transition to Mixed Sizes upon user approval if he tries to change individual tile size (show modal)
    const shouldSwitchToMixedSizes =
      nonCenterpieceTiles.length > 1 &&
      oldTileSize !== MIXED_SIZES &&
      isSingleSizeOrder(nonCenterpieceTiles) &&
      newTileSize !== oldTileSize

    if (shouldSwitchToMixedSizes) {
      const oldDiscountInfo = fetchDiscount({
        tileSize: oldTileSize,
        materialType: bulkCustomization.materialType,
        tiles: nonCenterpieceTiles,
      })
      if (
        isMixedSizesDiscount(discountId) ||
        [DISCOUNT_TYPES.PERCENT, DISCOUNT_TYPES.BUYOVER].includes(
          oldDiscountInfo.value?.type
        )
      ) {
        updateBulk.tileSize(MIXED_SIZES)
        updateSize()
      } else {
        saveTilesState()
        updateSize()
        updateBulk.tileSize(MIXED_SIZES)
      }
    } else {
      updateSize()
    }
  }

  const verifyStock = ({ tile, tileSize, materialType }) => {
    const tileSizeSupportedFrames = getSupportedFramesForSize({
      size: tileSize,
      materialType,
    })
    const frameColor = isFramedMaterial(materialType)
      ? getTileStyleData(tile).frameColor
      : FRAME_COLORS.NONE
    const oldTileSize = bulkCustomization.tileSize
    if (!tileSizeSupportedFrames.includes(frameColor)) {
      saveTilesState()
      notifyColorInSizeUnavailable({
        newSize: tileSize,
        oldTileSize,
        oldFrameColor: frameColor,
        materialType,
        unsupportedTiles: [tile],
      })
    }
  }

  const notifyColorInSizeUnavailable = ({
    newSize,
    oldTileSize,
    oldFrameColor,
    materialType,
    unsupportedTiles,
  }) => {
    const unsupportedUsedFrameColors = uniq(
      unsupportedTiles.map(
        (unsupportedTile) => getTileStyleData(unsupportedTile).frameColor
      )
    )
    saveTilesState()

    let snackbarTitle
    let snackbarSubtitle
    if (unsupportedUsedFrameColors.length > 1) {
      snackbarTitle = t(
        'out_of_stock.we_changed_some_frame_that_werent_available'
      )
    } else {
      const sizeName = getSizeName(newSize)
      const frameColorName = t(
        `order.frame_colors.${unsupportedUsedFrameColors[0]}`
      )

      const tileSizeSupportedFrames = getSupportedFramesForSize({
        size: newSize,
        materialType,
      })
      const newFrameColorName = t(
        `order.frame_colors.${tileSizeSupportedFrames[0]}`
      )

      snackbarTitle = t(
        'out_of_stock.x_x_isnt_available_color_changed_to_x_title',
        {
          v1: sizeName,
          v2: frameColorName.toLowerCase(),
        }
      )
      snackbarSubtitle = t(
        'out_of_stock.x_x_isnt_available_color_changed_to_x_subtitle',
        {
          v1: newFrameColorName.toLowerCase(),
        }
      )
    }

    notifyOutOfStock({
      name: OUT_OF_STOCK_PROPERTY.FRAME,
      title: snackbarTitle,
      subtitle: snackbarSubtitle,
      showUndo: true,
      undoCallback: () =>
        revertSizeState({
          prevSize: oldTileSize,
          currentSize: newSize,
          type: OUT_OF_STOCK,
        }),
      type: OUT_OF_STOCK_NOTIFICATION_TYPE.NOT_AVAILABLE,
      missingMaterial: materialType,
      missingSize: newSize,
      missingFrame:
        unsupportedUsedFrameColors.length > 1
          ? unsupportedUsedFrameColors
          : unsupportedUsedFrameColors[0],
      previousMaterial: materialType,
      previousSize: oldTileSize,
      previousFrame: oldFrameColor,
    })
  }

  const revertSizeState = ({ prevSize, currentSize, type }) => {
    analytics.track(EVENT_NAMES.UNDO_TAPPED, {
      Type: type,
      'Previous Size': prevSize,
      'Previous Material': bulkCustomization.materialType,
      'Current Size': currentSize,
      'Current Material': bulkCustomization.materialType,
    })
    updateSize({ relevantTiles: tiles, newSize: prevSize })
    undoTiles()
    closeSnackbar()
  }

  function updateSize({ relevantTiles, newSize }) {
    updateBulk.tileSize(newSize)
    if (newSize !== MIXED_SIZES) {
      const reloadingTiles = relevantTiles.filter(
        (tile) => isCollage(tile) && shouldUpdateCollageLayout(tile, newSize)
      )
      updateTiles(reloadingTiles, (t) => ({ ...t, isProcessing: true }))
      bulkCustomization.materialType === MIXED_MATERIALS
        ? relevantTiles.map((tile) => changeTileSize(tile.id, newSize))
        : changeTileSizeToAll(newSize)
    }
  }

  const notifyUnavailabilityInMixedMaterial = ({
    relevantTiles,
    newSize,
    oldSize,
  }) => {
    const usedSupportedMaterials = getTilesMaterials(relevantTiles)
    const usedUnsupportedMaterials = getTilesMaterials(
      difference(tiles, relevantTiles)
    )
    saveTilesState()
    const undoCallBack = () =>
      revertSizeState({
        prevSize: oldSize,
        currentSize: newSize,
        type: OUT_OF_STOCK,
      })
    // Mixed materials has at least two materials
    if (usedSupportedMaterials.length === 1) {
      notifyOutOfStock({
        name: `Mixed Materials - ${newSize}`,
        title: t('out_of_stock.x_only_available_in_x', {
          v1: getSizeName(newSize),
          v2: materialToName[usedSupportedMaterials[0]].toLowerCase(),
        }),
        subtitle: t('out_of_stock.applied_to_x', {
          v1: materialToName[usedSupportedMaterials[0]].toLowerCase(),
        }),
        showUndo: true,
        undoCallback: undoCallBack,
      })
    } else if (usedUnsupportedMaterials.length === 1) {
      notifyOutOfStock({
        name: `Mixed Materials - ${newSize}`,
        title: t('out_of_stock.x_isnt_available_in_x', {
          v1: getSizeName(newSize),
          v2: materialToName[usedUnsupportedMaterials[0]].toLowerCase(),
        }),
        subtitle: t('out_of_stock.applied_to_other_materials'),
        showUndo: true,
        undoCallback: undoCallBack,
      })
    } else if (
      usedSupportedMaterials.length > 1 &&
      usedUnsupportedMaterials.length > 1
    ) {
      notifyOutOfStock({
        name: `Mixed Materials - ${newSize}`,
        title: t('out_of_stock.x_available_in_selected_materials', {
          v1: getSizeName(newSize),
        }),
        subtitle: t('out_of_stock.applied_where_available'),
        showUndo: true,
        undoCallback: undoCallBack,
      })
    } else {
      return false
    }
    return true
  }

  const updateSizeAndNotify = async ({ newSize, materialType }) => {
    const oldTileSize = bulkCustomization.tileSize
    const oldFrameColor = bulkCustomization.frameColor
    const unsupportedTiles = getUnsupportedTilesForSize({
      size: newSize,
      tiles,
      materialType,
    })
    if (unsupportedTiles.length > 0) {
      saveTilesState()
    }

    let relevantTiles = [...tiles]
    let toastTriggered = false

    if (
      newSize !== MIXED_SIZES &&
      bulkCustomization.materialType === MIXED_MATERIALS
    ) {
      relevantTiles = tiles.filter((tile) =>
        isSupportedSizeForMaterial({
          size: newSize,
          material: tile.materialType,
        })
      )
      toastTriggered = notifyUnavailabilityInMixedMaterial({
        relevantTiles,
        newSize,
        oldSize: oldTileSize,
      })
    }

    updateSize({ relevantTiles, newSize })

    if (unsupportedTiles.length > 0) {
      if (toastTriggered) {
        analytics.track(EVENT_NAMES.OUT_OF_STOCK_SUPPRESSED, {
          unsupportedTiles: unsupportedTiles.map((unsupportedTile) => {
            const isFramedTile = isFramedMaterial(unsupportedTile.materialType)
            return {
              material: unsupportedTile.materialType,
              size: newSize,
              frame: isFramedTile
                ? unsupportedTile.customStyle.frameColor
                : null,
            }
          }),
        })
      } else {
        await new Promise((resolve) => setTimeout(resolve, 300))
        const ref = getRef({ tileId: unsupportedTiles[0].id })
        if (ref.current) {
          scrollIntoView(ref.current, {
            behavior: 'smooth',
            scrollMode: 'always',
            inline: 'center',
          })
        }
        notifyColorInSizeUnavailable({
          newSize,
          oldTileSize,
          oldFrameColor,
          materialType,
          unsupportedTiles,
        })
      }
    }
  }

  const updateBulkSize = (
    newSize,
    materialType,
    { onApprove, skipDialog = false } = {}
  ) => {
    if (bulkCustomization.tileSize === MIXED_SIZES && !skipDialog) {
      dialog.open(
        <BulkOverrideWarningDialog
          open
          onDismiss={() => dialog.close()}
          onApproved={() => {
            updateSizeAndNotify({ newSize, materialType })
            onApprove?.()
          }}
          ctaText={t('designer.bulk_override_warning.apply_to_all')}
          customization={TILE_CUSTOMIZATION.SIZE}
        />
      )
    } else {
      onApprove?.()
      updateSizeAndNotify({ newSize, materialType })
    }
  }

  return {
    updateBulkSize,
    updateTileSize,
    matchTilesSizeWithTiles,
    revertSizeState,
    tileSize: bulkCustomization.tileSize,
  }
}
